/**
 * Banner Component - Constants & Configuration
 * =============================================
 *
 * Constantes y tipos para el componente Banner.
 * Un banner promocional horizontal con icono, texto y CTA.
 */

import { getIcon } from '../../icons';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Variantes de color disponibles
 */
export type BannerVariant = 'default' | 'prime' | 'info' | 'warning' | 'success';

/**
 * Tamaños disponibles
 */
export type BannerSize = 'sm' | 'md' | 'lg';

/**
 * Tipos de icono predefinidos
 */
export type BannerIconType = 'trophy' | 'calendar' | 'star' | 'user' | 'bell' | 'info' | 'check' | 'warning' | 'gift' | 'custom';

/**
 * Posición del icono
 */
export type IconPosition = 'left' | 'top';

/**
 * Estilos de variante
 */
export interface VariantStyles {
  container: string;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  button: string;
  buttonHover: string;
}

/**
 * Estilos de tamaño
 */
export interface SizeStyles {
  container: string;
  icon: string;
  iconWrapper: string;
  title: string;
  description: string;
  button: string;
  gap: string;
}

/**
 * Valores por defecto
 */
export interface DefaultValues {
  variant: BannerVariant;
  size: BannerSize;
  iconType: BannerIconType;
  iconPosition: IconPosition;
  dismissible: boolean;
  showIcon: boolean;
  showButton: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Variantes de color
 */
export const VARIANTS: Record<BannerVariant, VariantStyles> = {
  default: {
    container: 'ec-bg-color-surface ec-border ec-border-color-border',
    iconBg: 'ec-bg-color-primary-light',
    iconColor: 'ec-text-color-primary',
    title: 'ec-text-color-text',
    description: 'ec-text-color-text-muted',
    button: 'ec-bg-color-primary ec-text-color-primary-contrast',
    buttonHover: 'hover:ec-bg-color-primary-dark'
  },
  prime: {
    container: 'ec-bg-gradient-to-r ec-from-prime ec-to-prime-dark',
    iconBg: 'ec-bg-white/20',
    iconColor: 'ec-text-white',
    title: 'ec-text-white',
    description: 'ec-text-white/80',
    button: 'ec-bg-white ec-text-prime',
    buttonHover: 'hover:ec-bg-white/90'
  },
  info: {
    container: 'ec-bg-blue-50 ec-border ec-border-blue-200',
    iconBg: 'ec-bg-blue-100',
    iconColor: 'ec-text-blue-600',
    title: 'ec-text-blue-900',
    description: 'ec-text-blue-700',
    button: 'ec-bg-blue-600 ec-text-white',
    buttonHover: 'hover:ec-bg-blue-700'
  },
  warning: {
    container: 'ec-bg-amber-50 ec-border ec-border-amber-200',
    iconBg: 'ec-bg-amber-100',
    iconColor: 'ec-text-amber-600',
    title: 'ec-text-amber-900',
    description: 'ec-text-amber-700',
    button: 'ec-bg-amber-600 ec-text-white',
    buttonHover: 'hover:ec-bg-amber-700'
  },
  success: {
    container: 'ec-bg-green-50 ec-border ec-border-green-200',
    iconBg: 'ec-bg-green-100',
    iconColor: 'ec-text-green-600',
    title: 'ec-text-green-900',
    description: 'ec-text-green-700',
    button: 'ec-bg-green-600 ec-text-white',
    buttonHover: 'hover:ec-bg-green-700'
  }
};

/**
 * Tamaños disponibles
 */
export const SIZES: Record<BannerSize, SizeStyles> = {
  sm: {
    container: 'ec-p-3 ec-rounded-lg',
    icon: 'ec-w-5 ec-h-5',
    iconWrapper: 'ec-w-10 ec-h-10',
    title: 'ec-text-sm ec-font-semibold',
    description: 'ec-text-xs',
    button: 'ec-px-3 ec-py-2 ec-text-xs',
    gap: 'ec-gap-3'
  },
  md: {
    container: 'ec-p-4 ec-rounded-2xl',
    icon: 'ec-w-6 ec-h-6',
    iconWrapper: 'ec-w-12 ec-h-12',
    title: 'ec-text-base ec-font-semibold',
    description: 'ec-text-sm',
    button: 'ec-px-4 ec-py-2 ec-text-sm',
    gap: 'ec-gap-4'
  },
  lg: {
    container: 'ec-p-6 ec-rounded-2xl',
    icon: 'ec-w-8 ec-h-8',
    iconWrapper: 'ec-w-16 ec-h-16',
    title: 'ec-text-lg ec-font-bold',
    description: 'ec-text-base',
    button: 'ec-px-6 ec-py-3 ec-text-base',
    gap: 'ec-gap-6'
  }
};

/**
 * Iconos SVG predefinidos
 *
 * Las keys públicas (iconType="trophy", etc.) se mantienen igual — cero
 * breaking. Los valores vienen del registro central (`src/icons`); `bell`
 * no tiene equivalente en Figma y usa su SVG legacy vía `getIcon()`.
 * `custom` sigue vacío: es el passthrough de `customIcon`.
 */
export const ICONS: Record<BannerIconType, string> = {
  trophy: getIcon('trophy') || '',
  calendar: getIcon('calendar-empty') || '',
  star: getIcon('star') || '',
  user: getIcon('user') || '',
  bell: getIcon('bell') || '',
  info: getIcon('circular-info') || '',
  check: getIcon('check') || '',
  warning: getIcon('triangular-alert') || '',
  gift: getIcon('gift') || '',
  custom: ''
};

/**
 * Valores por defecto
 */
export const DEFAULTS: DefaultValues = {
  variant: 'default',
  size: 'md',
  iconType: 'trophy',
  iconPosition: 'left',
  dismissible: false,
  showIcon: true,
  showButton: true
};

/**
 * Clases base
 */
export const BASE_CLASSES = {
  container: 'ec-flex ec-items-center ec-shadow-sm ec-transition-all ec-duration-200',
  containerVertical: 'ec-flex ec-flex-col ec-items-center ec-text-center ec-shadow-sm ec-transition-all ec-duration-200',
  iconWrapper: 'ec-flex-shrink-0 ec-rounded-full ec-flex ec-items-center ec-justify-center',
  content: 'ec-flex-1 ec-min-w-0',
  button: 'ec-flex-shrink-0 ec-font-medium ec-rounded-lg ec-transition-colors ec-whitespace-nowrap'
};
